import React, { useState, useMemo } from 'react';
import { AthleteProfile } from '../types';
import { US_STATES, getCitiesForState } from '../data/usLocations';
import { ShareConfirmationNotification, ShareConfirmationData } from './notifications/ShareConfirmationNotification';
import {
  Trophy,
  Award,
  Search,
  Filter,
  Share2,
  Flame,
  Users,
  Sparkles,
  Crown,
  Zap,
  ArrowRightLeft,
  X,
  Target,
  Shield,
  Zap as ZapIcon,
  CheckCircle2
} from 'lucide-react';

interface VolleyballLeaderboardViewProps {
  athletes: AthleteProfile[];
  currentUser: AthleteProfile;
  onShareAthlete?: (athlete: AthleteProfile) => void;
}

export const VolleyballLeaderboardView: React.FC<VolleyballLeaderboardViewProps> = ({
  athletes,
  currentUser,
  onShareAthlete,
}) => {
  const [metricFilter, setMetricFilter] = useState<'kills_blocks' | 'kills' | 'blocks' | 'aces' | 'digs'>('kills_blocks');
  const [positionFilter, setPositionFilter] = useState<string>('all');
  const [friendsOnlyFilter, setFriendsOnlyFilter] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [shareConfirmation, setShareConfirmation] = useState<ShareConfirmationData | null>(null);

  const handleShareAthlete = (athlete: AthleteProfile) => {
    if (onShareAthlete) {
      onShareAthlete(athlete);
    }
    setShareConfirmation({
      type: 'leaderboard_rank',
      title: `Volleyball Rank Card Shared for ${athlete.name}`,
      subtitle: `Lvl ${athlete.level} Volleyball Athlete • ${athlete.xp} XP • Verified Leaderboard Card link copied!`,
      sport: 'volleyball',
      xpAwarded: 15,
    });
  };

  // Friends set
  const friendIds = useMemo(() => {
    if (!currentUser?.friendsList) return new Set([currentUser?.id || '']);
    const ids = currentUser.friendsList.map((f) => (typeof f === 'string' ? f : f.id));
    return new Set([currentUser?.id || '', ...ids]);
  }, [currentUser?.friendsList, currentUser?.id]);

  // Comparison State
  const [compareList, setCompareList] = useState<AthleteProfile[]>([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  // Filter & Sort Athletes specifically for Volleyball
  const volleyballAthletes = useMemo(() => {
    // Filter athletes where primarySport is volleyball OR they have recorded volleyball stats
    const filtered = athletes.filter((a) => {
      const isVolleyPrimary = a.primarySport === 'volleyball';
      const vStats = a.stats?.volleyball;
      const hasVolleyStats = vStats && ((vStats.kills || 0) > 0 || (vStats.gamesPlayed || 0) > 0 || (vStats.blocks || 0) > 0);
      return isVolleyPrimary || hasVolleyStats;
    });

    // Location & Friends filter
    const matchesLocation = filtered.filter((a) => {
      if (friendsOnlyFilter && !friendIds.has(a.id)) {
        return false;
      }
      if (stateFilter !== 'all') {
        const aState = (a.registeredState || a.location?.state || '').toLowerCase();
        const aLocCity = (a.location?.city || '').toLowerCase();
        const aSchool = (a.schoolOrLeague || '').toLowerCase();
        const sQuery = stateFilter.toLowerCase();
        const matchesS = aState === sQuery || aLocCity.includes(sQuery) || aSchool.includes(sQuery);
        if (!matchesS) return false;
      }
      if (cityFilter !== 'all') {
        const aCity = (a.registeredCity || a.location?.city || '').toLowerCase();
        const aSchool = (a.schoolOrLeague || '').toLowerCase();
        const cQuery = cityFilter.toLowerCase();
        const matchesC = aCity.includes(cQuery) || aSchool.includes(cQuery);
        if (!matchesC) return false;
      }
      return true;
    });

    // Position filter
    const matchesPosition = matchesLocation.filter((a) => {
      if (positionFilter === 'all') return true;
      return a.position?.toLowerCase().includes(positionFilter.toLowerCase());
    });

    // Search filter
    const matchesSearch = matchesPosition.filter((a) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        a.name.toLowerCase().includes(q) ||
        (a.schoolOrLeague && a.schoolOrLeague.toLowerCase().includes(q)) ||
        (a.position && a.position.toLowerCase().includes(q)) ||
        (a.registeredCity && a.registeredCity.toLowerCase().includes(q)) ||
        (a.registeredState && a.registeredState.toLowerCase().includes(q))
      );
    });

    // Sort based on chosen metric
    return matchesSearch.sort((a, b) => {
      const statsA = a.stats?.volleyball || { kills: 0, blocks: 0, aces: 0, digs: 0, gamesPlayed: 0 };
      const statsB = b.stats?.volleyball || { kills: 0, blocks: 0, aces: 0, digs: 0, gamesPlayed: 0 };

      if (metricFilter === 'kills_blocks') {
        const totalA = (statsA.kills || 0) + (statsA.blocks || 0);
        const totalB = (statsB.kills || 0) + (statsB.blocks || 0);
        if (totalB !== totalA) return totalB - totalA;
        return (statsB.kills || 0) - (statsA.kills || 0);
      }
      if (metricFilter === 'kills') {
        return (statsB.kills || 0) - (statsA.kills || 0);
      }
      if (metricFilter === 'blocks') {
        return (statsB.blocks || 0) - (statsA.blocks || 0);
      }
      if (metricFilter === 'aces') {
        return (statsB.aces || 0) - (statsA.aces || 0);
      }
      if (metricFilter === 'digs') {
        return (statsB.digs || 0) - (statsA.digs || 0);
      }
      return 0;
    });
  }, [athletes, metricFilter, positionFilter, searchQuery, stateFilter, cityFilter]);

  const topThree = volleyballAthletes.slice(0, 3);

  const toggleCompare = (athlete: AthleteProfile) => {
    if (compareList.some((a) => a.id === athlete.id)) {
      setCompareList(compareList.filter((a) => a.id !== athlete.id));
    } else {
      if (compareList.length >= 2) {
        setCompareList([compareList[1], athlete]);
      } else {
        setCompareList([...compareList, athlete]);
      }
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn text-white pb-12">
      {/* Volleyball Leaderboard Banner */}
      <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 p-6 md:p-8 border border-white/10 shadow-2xl">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-lime-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-60 h-60 bg-cyan-400/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-lime-400 text-black font-black italic text-xs uppercase rounded-full shadow-md">
                OFFICIAL VOLLEYBALL RANKINGS 🏐
              </span>
              <span className="text-xs text-indigo-200/80 font-bold uppercase tracking-wider">
                • Verified Spike & Block Stats
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black italic uppercase tracking-tight text-white flex items-center gap-3">
              <span>Volleyball Leaderboard</span>
              <Trophy className="w-9 h-9 text-lime-400 stroke-[2.5]" />
            </h1>
            <p className="text-sm text-indigo-200/80 max-w-2xl font-medium">
              Rankings exclusively for registered volleyball athletes. Sorted dynamically by cumulative Spike Kills, Net Blocks, Service Aces, and Defensive Digs across all verified sets.
            </p>
          </div>

          {/* Quick Stat Pill */}
          <div className="bg-indigo-900/80 backdrop-blur-md p-4 rounded-2xl border border-white/10 flex items-center space-x-4 shadow-xl shrink-0">
            <div className="p-3 bg-lime-400 text-black rounded-xl font-black">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-black tracking-widest text-indigo-300">Your Volleyball Rank</div>
              <div className="text-xl font-black italic text-lime-400">
                #{volleyballAthletes.findIndex((a) => a.id === currentUser.id) + 1 || 'Unranked'}
              </div>
              <div className="text-[11px] text-indigo-200/70 font-semibold">
                {currentUser.stats?.volleyball?.kills || 0} Kills • {currentUser.stats?.volleyball?.blocks || 0} Blocks
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top 3 Podium Highlights */}
      {topThree.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
          {/* 2nd Place */}
          {topThree[1] && (
            <div className="order-2 md:order-1 bg-indigo-950/80 rounded-[2rem] border border-cyan-400/30 p-6 flex flex-col items-center text-center relative overflow-hidden shadow-xl hover:border-cyan-400 transition">
              <div className="absolute top-3 right-3 px-3 py-1 bg-cyan-400/20 border border-cyan-400/40 text-cyan-300 font-black italic text-xs rounded-full">
                #2 SILVER 🥈
              </div>
              <img
                src={topThree[1].avatar}
                alt={topThree[1].name}
                className="w-20 h-20 rounded-2xl object-cover ring-4 ring-cyan-400/60 shadow-lg mb-3"
                referrerPolicy="no-referrer"
              />
              <h3 className="text-lg font-black italic text-white uppercase">{topThree[1].name}</h3>
              <p className="text-xs text-indigo-200/70 font-bold mb-3">{topThree[1].position} • {topThree[1].schoolOrLeague}</p>
              <div className="w-full bg-indigo-900/60 p-3 rounded-xl border border-white/5 space-y-1">
                <div className="text-2xl font-black italic text-cyan-300 font-mono">
                  {topThree[1].stats?.volleyball?.kills || 0} KILLS
                </div>
                <div className="text-[11px] text-indigo-200/80 font-bold">
                  {topThree[1].stats?.volleyball?.blocks || 0} Blocks • {topThree[1].stats?.volleyball?.aces || 0} Aces
                </div>
              </div>
            </div>
          )}

          {/* 1st Place Champion */}
          {topThree[0] && (
            <div className="order-1 md:order-2 bg-gradient-to-b from-indigo-900 to-indigo-950 rounded-[2.5rem] border-2 border-lime-400 p-7 flex flex-col items-center text-center relative overflow-hidden shadow-2xl shadow-lime-400/10 transform scale-105">
              <div className="absolute top-0 right-0 left-0 h-1 bg-lime-400 shadow-md shadow-lime-400" />
              <div className="absolute top-3 right-3 px-3.5 py-1 bg-lime-400 text-black font-black italic text-xs rounded-full shadow-lg flex items-center gap-1">
                <Crown className="w-3.5 h-3.5 fill-black" />
                <span>#1 CHAMPION 🥇</span>
              </div>
              <img
                src={topThree[0].avatar}
                alt={topThree[0].name}
                className="w-24 h-24 rounded-2xl object-cover ring-4 ring-lime-400 shadow-2xl mb-3 mt-2"
                referrerPolicy="no-referrer"
              />
              <h3 className="text-xl font-black italic text-white uppercase">{topThree[0].name}</h3>
              <p className="text-xs text-lime-400 font-extrabold mb-4">{topThree[0].position} • {topThree[0].schoolOrLeague}</p>
              <div className="w-full bg-indigo-950 p-4 rounded-2xl border border-lime-400/30 space-y-1 shadow-inner">
                <div className="text-3xl font-black italic text-lime-400 font-mono">
                  {topThree[0].stats?.volleyball?.kills || 0} KILLS
                </div>
                <div className="text-xs text-indigo-200 font-bold">
                  {topThree[0].stats?.volleyball?.blocks || 0} Blocks • {topThree[0].stats?.volleyball?.aces || 0} Aces • {topThree[0].stats?.volleyball?.digs || 0} Digs
                </div>
              </div>
            </div>
          )}

          {/* 3rd Place */}
          {topThree[2] && (
            <div className="order-3 bg-indigo-950/80 rounded-[2rem] border border-amber-400/30 p-6 flex flex-col items-center text-center relative overflow-hidden shadow-xl hover:border-amber-400 transition">
              <div className="absolute top-3 right-3 px-3 py-1 bg-amber-400/20 border border-amber-400/40 text-amber-300 font-black italic text-xs rounded-full">
                #3 BRONZE 🥉
              </div>
              <img
                src={topThree[2].avatar}
                alt={topThree[2].name}
                className="w-20 h-20 rounded-2xl object-cover ring-4 ring-amber-400/60 shadow-lg mb-3"
                referrerPolicy="no-referrer"
              />
              <h3 className="text-lg font-black italic text-white uppercase">{topThree[2].name}</h3>
              <p className="text-xs text-indigo-200/70 font-bold mb-3">{topThree[2].position} • {topThree[2].schoolOrLeague}</p>
              <div className="w-full bg-indigo-900/60 p-3 rounded-xl border border-white/5 space-y-1">
                <div className="text-2xl font-black italic text-amber-300 font-mono">
                  {topThree[2].stats?.volleyball?.kills || 0} KILLS
                </div>
                <div className="text-[11px] text-indigo-200/80 font-bold">
                  {topThree[2].stats?.volleyball?.blocks || 0} Blocks • {topThree[2].stats?.volleyball?.aces || 0} Aces
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Control Bar: Metric Sort Tabs & Search / Position Filter */}
      <div className="bg-indigo-950/90 p-4 rounded-3xl border border-white/10 space-y-4 shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Metric Sort Tabs */}
          <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 md:pb-0">
            <button
              onClick={() => setMetricFilter('kills_blocks')}
              className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition whitespace-nowrap ${
                metricFilter === 'kills_blocks' ? 'bg-lime-400 text-black shadow-lg' : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              Kills + Blocks 💥🛡️
            </button>
            <button
              onClick={() => setMetricFilter('kills')}
              className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition whitespace-nowrap ${
                metricFilter === 'kills' ? 'bg-lime-400 text-black shadow-lg' : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              Spike Kills 💥
            </button>
            <button
              onClick={() => setMetricFilter('blocks')}
              className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition whitespace-nowrap ${
                metricFilter === 'blocks' ? 'bg-lime-400 text-black shadow-lg' : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              Net Blocks 🛡️
            </button>
            <button
              onClick={() => setMetricFilter('aces')}
              className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition whitespace-nowrap ${
                metricFilter === 'aces' ? 'bg-lime-400 text-black shadow-lg' : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              Service Aces ⚡
            </button>
            <button
              onClick={() => setMetricFilter('digs')}
              className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition whitespace-nowrap ${
                metricFilter === 'digs' ? 'bg-lime-400 text-black shadow-lg' : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              Digs 🥞
            </button>
          </div>

          {/* Search Box & Position Filter */}
          <div className="flex items-center space-x-3 shrink-0">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 text-indigo-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search volleyball player..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-indigo-900/80 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-xs font-medium text-white placeholder-indigo-300/50 outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              className="bg-indigo-900/80 border border-white/10 rounded-xl px-3 py-2 text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
            >
              <option value="all">All Positions</option>
              <option value="Hitter">Outside / Opposite Hitter</option>
              <option value="Setter">Setter</option>
              <option value="Middle">Middle Blocker</option>
              <option value="Libero">Libero / DS</option>
            </select>

            {/* Friends-Only Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setFriendsOnlyFilter(!friendsOnlyFilter)}
              className={`px-3 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 border shrink-0 ${
                friendsOnlyFilter
                  ? 'bg-lime-400 text-black border-lime-300 shadow-lg shadow-lime-400/20 ring-2 ring-lime-400/50'
                  : 'bg-indigo-900/80 text-indigo-200 border-white/10 hover:text-white hover:border-white/20'
              }`}
              title="Filter volleyball stats exclusively against your added friends"
            >
              <Users className={`w-3.5 h-3.5 ${friendsOnlyFilter ? 'text-black' : 'text-lime-400'}`} />
              <span>{friendsOnlyFilter ? 'Friends League 👥' : 'Friends Only'}</span>
            </button>
          </div>
        </div>

        {/* Location / Regional Filters (State & City) */}
        <div className="pt-3 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center space-x-2 overflow-x-auto pb-1 md:pb-0 text-xs">
            <span className="text-[10px] uppercase font-black tracking-widest text-cyan-300 shrink-0">
              Regional View:
            </span>

            <button
              type="button"
              onClick={() => {
                if (currentUser.registeredCity) {
                  setCityFilter(currentUser.registeredCity);
                  setStateFilter('all');
                } else {
                  setCityFilter('New York');
                  setStateFilter('NY');
                }
              }}
              className={`px-3 py-1.5 rounded-xl font-black transition whitespace-nowrap flex items-center space-x-1 ${
                cityFilter !== 'all'
                  ? 'bg-cyan-400 text-black shadow-md shadow-cyan-400/20'
                  : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              <span>📍 Local ({currentUser.registeredCity || 'City'})</span>
            </button>

            <button
              type="button"
              onClick={() => {
                if (currentUser.registeredState) {
                  setStateFilter(currentUser.registeredState);
                  setCityFilter('all');
                } else {
                  setStateFilter('NY');
                  setCityFilter('all');
                }
              }}
              className={`px-3 py-1.5 rounded-xl font-black transition whitespace-nowrap flex items-center space-x-1 ${
                stateFilter !== 'all' && cityFilter === 'all'
                  ? 'bg-cyan-400 text-black shadow-md shadow-cyan-400/20'
                  : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              <span>🏛️ Statewide ({currentUser.registeredState || 'State'})</span>
            </button>

            <button
              type="button"
              onClick={() => { setStateFilter('all'); setCityFilter('all'); }}
              className={`px-3 py-1.5 rounded-xl font-black transition whitespace-nowrap flex items-center space-x-1 ${
                stateFilter === 'all' && cityFilter === 'all'
                  ? 'bg-cyan-400 text-black shadow-md shadow-cyan-400/20'
                  : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
              }`}
            >
              <span>🇺🇸 National (All US)</span>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            {/* Custom State Dropdown */}
            <select
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value);
                setCityFilter('all');
              }}
              className="bg-indigo-900/90 border border-white/10 rounded-xl px-2.5 py-1.5 text-xs font-bold text-cyan-300 outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <option value="all">Filter State (All States)</option>
              {US_STATES.map((st) => (
                <option key={st.code} value={st.code} className="bg-indigo-950 text-white">
                  {st.name} ({st.code})
                </option>
              ))}
            </select>

            {/* Dynamic City Dropdown */}
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="bg-indigo-900/90 border border-white/10 rounded-xl px-2.5 py-1.5 text-xs font-bold text-lime-400 outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <option value="all">
                {stateFilter === 'all' ? 'City / Town (All Cities)' : `All Cities in ${stateFilter}`}
              </option>
              {(stateFilter !== 'all' ? getCitiesForState(stateFilter) : ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami', 'San Francisco', 'Dallas', 'Seattle', 'Atlanta', 'Boston', 'Philadelphia', 'Phoenix']).map((city) => (
                <option key={city} value={city} className="bg-indigo-950 text-white">
                  {city}
                </option>
              ))}
            </select>
          </div>
        </div>

        {compareList.length > 0 && (
          <div className="p-3 bg-indigo-900/80 rounded-2xl border border-lime-400/40 flex items-center justify-between">
            <div className="flex items-center space-x-2 text-xs">
              <ArrowRightLeft className="w-4 h-4 text-lime-400" />
              <span className="font-bold text-white">Compare Selected ({compareList.length}/2):</span>
              <div className="flex items-center gap-2">
                {compareList.map((a) => (
                  <span key={a.id} className="bg-black/40 px-2.5 py-1 rounded-lg text-lime-300 font-extrabold flex items-center gap-1">
                    <span>{a.name}</span>
                    <X className="w-3 h-3 cursor-pointer hover:text-rose-400" onClick={() => toggleCompare(a)} />
                  </span>
                ))}
              </div>
            </div>

            {compareList.length === 2 && (
              <button
                onClick={() => setIsCompareModalOpen(true)}
                className="px-4 py-1.5 bg-lime-400 text-black font-black italic uppercase text-xs rounded-xl shadow-md"
              >
                Launch Volleyball Head-to-Head ⚔️
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main Volleyball Athletes Table */}
      <div className="bg-indigo-950/90 rounded-[2.5rem] border border-white/10 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-indigo-900/80 text-[10px] font-black italic uppercase tracking-wider text-indigo-300 border-b border-white/10">
                <th className="px-6 py-4">Rank</th>
                <th className="px-6 py-4">Volleyball Athlete</th>
                <th className="px-6 py-4">Position</th>
                <th className="px-6 py-4 text-lime-400">Spike Kills 💥</th>
                <th className="px-6 py-4 text-cyan-300">Net Blocks 🛡️</th>
                <th className="px-6 py-4 text-amber-300">Service Aces ⚡</th>
                <th className="px-6 py-4 text-purple-300">Digs 🥞</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-sm">
              {volleyballAthletes.map((athlete, idx) => {
                const rank = idx + 1;
                const vStats = athlete.stats?.volleyball || { kills: 0, blocks: 0, aces: 0, digs: 0, gamesPlayed: 0 };
                const isSelectedForCompare = compareList.some((c) => c.id === athlete.id);
                const isMe = athlete.id === currentUser.id;

                return (
                  <tr
                    key={athlete.id}
                    className={`hover:bg-indigo-900/40 transition ${
                      isMe ? 'bg-lime-400/10 border-l-4 border-lime-400' : ''
                    }`}
                  >
                    <td className="px-6 py-4 font-mono font-black italic">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-xl font-bold ${
                        rank === 1
                          ? 'bg-lime-400 text-black shadow-lg'
                          : rank === 2
                          ? 'bg-cyan-400 text-black'
                          : rank === 3
                          ? 'bg-amber-400 text-black'
                          : 'bg-indigo-900 text-indigo-200'
                      }`}>
                        #{rank}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <img
                          src={athlete.avatar}
                          alt={athlete.name}
                          className="w-10 h-10 rounded-xl object-cover ring-2 ring-white/10"
                          referrerPolicy="no-referrer"
                        />
                        <div>
                          <div className="font-black italic text-white flex items-center gap-1.5">
                            <span>{athlete.name}</span>
                            {isMe && (
                              <span className="px-2 py-0.5 bg-lime-400 text-black text-[9px] font-black rounded-md uppercase">
                                YOU
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-indigo-200/70 font-semibold">{athlete.schoolOrLeague || 'Volleyball Club'}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-indigo-900/80 border border-white/10 rounded-lg text-xs font-bold text-indigo-200">
                        {athlete.position || 'Volleyball Athlete'}
                      </span>
                    </td>

                    <td className="px-6 py-4 font-mono font-black text-lime-400 text-base">
                      {vStats.kills || 0}
                    </td>

                    <td className="px-6 py-4 font-mono font-black text-cyan-300 text-base">
                      {vStats.blocks || 0}
                    </td>

                    <td className="px-6 py-4 font-mono font-black text-amber-300 text-base">
                      {vStats.aces || 0}
                    </td>

                    <td className="px-6 py-4 font-mono font-black text-purple-300 text-base">
                      {vStats.digs || 0}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => toggleCompare(athlete)}
                          className={`p-2 rounded-xl text-xs font-extrabold transition border ${
                            isSelectedForCompare
                              ? 'bg-lime-400 text-black border-lime-400'
                              : 'bg-indigo-900 text-indigo-200 hover:text-white border-white/10'
                          }`}
                          title="Compare Athlete"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                        {onShareAthlete && (
                          <button
                            onClick={() => handleShareAthlete(athlete)}
                            className="p-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 hover:text-white rounded-xl border border-white/10 transition"
                            title="Share Profile"
                          >
                            <Share2 className="w-4 h-4 text-lime-400" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Head-to-Head Comparison Modal */}
      {isCompareModalOpen && compareList.length === 2 && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-indigo-950 border border-lime-400/40 rounded-[2.5rem] p-6 max-w-2xl w-full space-y-6 shadow-2xl relative text-white">
            <button
              onClick={() => setIsCompareModalOpen(false)}
              className="absolute top-4 right-4 p-2 bg-indigo-900 rounded-full text-indigo-300 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center space-y-1">
              <span className="px-3 py-1 bg-lime-400 text-black font-black italic text-xs uppercase rounded-full">
                VOLLEYBALL HEAD-TO-HEAD COMPARISON ⚔️
              </span>
              <h2 className="text-2xl font-black italic uppercase text-white">Player Stats Breakdown</h2>
            </div>

            <div className="grid grid-cols-2 gap-6 text-center">
              {compareList.map((player) => {
                const pStats = player.stats?.volleyball || { kills: 0, blocks: 0, aces: 0, digs: 0 };
                return (
                  <div key={player.id} className="bg-indigo-900/60 p-5 rounded-2xl border border-white/10 space-y-3">
                    <img
                      src={player.avatar}
                      alt={player.name}
                      className="w-16 h-16 mx-auto rounded-2xl object-cover ring-2 ring-lime-400"
                      referrerPolicy="no-referrer"
                    />
                    <div>
                      <h3 className="font-black italic text-white uppercase text-base">{player.name}</h3>
                      <p className="text-xs text-lime-400 font-bold">{player.position}</p>
                    </div>

                    <div className="space-y-2 pt-2 text-left font-mono text-xs">
                      <div className="flex justify-between bg-black/30 p-2 rounded-lg">
                        <span className="text-indigo-300 font-semibold">Spike Kills:</span>
                        <strong className="text-lime-400 font-black">{pStats.kills || 0}</strong>
                      </div>
                      <div className="flex justify-between bg-black/30 p-2 rounded-lg">
                        <span className="text-indigo-300 font-semibold">Net Blocks:</span>
                        <strong className="text-cyan-300 font-black">{pStats.blocks || 0}</strong>
                      </div>
                      <div className="flex justify-between bg-black/30 p-2 rounded-lg">
                        <span className="text-indigo-300 font-semibold">Service Aces:</span>
                        <strong className="text-amber-300 font-black">{pStats.aces || 0}</strong>
                      </div>
                      <div className="flex justify-between bg-black/30 p-2 rounded-lg">
                        <span className="text-indigo-300 font-semibold">Digs:</span>
                        <strong className="text-purple-300 font-black">{pStats.digs || 0}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Share Confirmation Feedback Notification Overlay */}
      <ShareConfirmationNotification
        data={shareConfirmation}
        onClose={() => setShareConfirmation(null)}
      />
    </div>
  );
};
